import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success('Password updated. Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. Request a new link.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-4 bg-card border border-border/50 rounded-lg text-center" data-testid="reset-missing-token">
          <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
          <h1 className="text-xl font-bold tracking-tight">Invalid reset link</h1>
          <p className="text-sm text-muted-foreground">This link is missing a token. Please request a new password reset.</p>
          <Link to="/forgot-password" className="inline-block text-sm text-primary hover:underline">Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border/50 rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="text-center">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="reset-title">RealProp</h1>
          <p className="text-muted-foreground mt-2">Choose a new password</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="password">New password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="h-12 bg-secondary/20 border-transparent focus:border-primary rounded-md pr-10"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                data-testid="toggle-reset-password"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters.</p>
          </div>

          <div>
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="h-12 bg-secondary/20 border-transparent focus:border-primary rounded-md"
              data-testid="reset-confirm-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-primary/90"
            data-testid="reset-submit-button"
          >
            {loading ? 'Updating…' : 'Update password'}
          </Button>
        </form>

        <div className="text-center text-sm">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid="back-to-login">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
